/**
 * CLS session windowing and shift attribution.
 *
 * Split out of WebVitalsReporter so the arithmetic is testable without a DOM
 * or a PerformanceObserver — the windowing rule is the part that is easy to
 * get subtly wrong and impossible to eyeball in production.
 */

export interface ShiftSource {
  node?: Element | null;
  previousRect?: { x: number; y: number; width: number; height: number };
  currentRect?: { x: number; y: number; width: number; height: number };
}

/** The shape of a `layout-shift` PerformanceEntry, which TS does not ship. */
export interface LayoutShiftLike {
  value: number;
  startTime: number;
  hadRecentInput: boolean;
  sources?: ShiftSource[];
}

export interface ClsSession {
  /** The worst session window's total — this IS the CLS metric. */
  value: number;
  /** Entries counted (excludes hadRecentInput). */
  count: number;
  /** The single largest shift value seen, for triage. */
  largest: number;
  /** The source of that largest shift, if the browser named one. */
  culprit: ShiftSource | null;
}

// Google's definition: a session window ends after a 1s gap between shifts, or
// once it has run for 5s, whichever comes first. CLS is the LARGEST such
// window, not the sum of all shifts — summing everything is the classic
// overstatement, and on a long-lived SPA view it is wrong by a lot.
const MAX_GAP_MS = 1000;
const MAX_WINDOW_MS = 5000;

export function computeClsSession(entries: LayoutShiftLike[]): ClsSession {
  // hadRecentInput shifts are user-initiated and excluded from CLS by
  // definition — a shift 500ms after a tap is the tap's doing.
  const counted = entries
    .filter((e) => !e.hadRecentInput)
    .sort((a, b) => a.startTime - b.startTime);

  let best = 0;
  let current = 0;
  let windowStart = 0;
  let previous = 0;

  let largest = 0;
  let culprit: ShiftSource | null = null;

  for (const e of counted) {
    const startsNewWindow =
      current === 0 ||
      e.startTime - previous > MAX_GAP_MS ||
      e.startTime - windowStart > MAX_WINDOW_MS;

    if (startsNewWindow) {
      current = e.value;
      windowStart = e.startTime;
    } else {
      current += e.value;
    }
    previous = e.startTime;
    if (current > best) best = current;

    if (e.value > largest) {
      largest = e.value;
      culprit = pickSource(e.sources);
    }
  }

  return { value: best, count: counted.length, largest, culprit };
}

/**
 * Does this element exist for the reader, or only for decoration?
 *
 * Decorative layers are excluded from attribution because they are the ones
 * most likely to be transform-animated, and a transform-animated element
 * reports enormous rect deltas without ever causing a layout shift.
 */
function isDecorative(node: Element | null | undefined): boolean {
  if (!node || typeof node !== 'object') return false;
  try {
    // ARIA-HIDDEN IS INHERITED, so the node's own attribute is the wrong test.
    // Both real cases on this codebase are a bare child inside a hidden
    // wrapper: `span.ambient-particle` sits in an aria-hidden particle layer,
    // and `span.caret-blink` sits in the aria-hidden TypewriterPlaceholder.
    // Checking getAttribute alone would have skipped neither.
    if (node.closest?.('[aria-hidden="true"]')) return true;
    const cls = typeof node.className === 'string' ? node.className : '';
    return /\bpointer-events-none\b/.test(cls);
  } catch {
    // closest() is missing on some node types; fall back to the own-attribute
    // test rather than treating an exception as "not decorative".
    try {
      return node.getAttribute?.('aria-hidden') === 'true';
    } catch {
      return false;
    }
  }
}

/**
 * The source that did the most damage within one shift.
 *
 * SCORED BY AREA x DISTANCE, not distance alone. This was wrong first time and
 * the field data caught it: `span.ambient-particle` was named as the culprit on
 * two views at CLS ~1.04, and those particles are aria-hidden, absolutely
 * positioned inside an overflow-hidden container, and animate only transform
 * and opacity — which cannot shift layout at all. They won on a
 * furthest-distance tiebreak because a particle drifting 110vh reports a huge
 * rect delta. The real culprit on those views was masked.
 *
 * Area x distance is also what CLS itself measures — impact fraction times
 * distance fraction — so this is the metric's own weighting rather than a
 * heuristic. A 2px particle scores 4 x 800 = 3,200; a full-width footer moving
 * 50px scores 72,000 x 50 = 3,600,000. The particle can no longer win.
 */
function pickSource(sources: ShiftSource[] | undefined): ShiftSource | null {
  if (!sources?.length) return null;
  const candidates = sources.filter((s) => !isDecorative(s.node));
  // If EVERY source was decorative, fall back to the full list rather than
  // returning null — "unknown" tells the reader less than a hedged name.
  const pool = candidates.length ? candidates : sources;

  let best: ShiftSource | null = null;
  let bestScore = -1;
  for (const s of pool) {
    const p = s.previousRect;
    const c = s.currentRect;
    if (!p || !c) continue;
    const distance = Math.abs(c.y - p.y) + Math.abs(c.x - p.x);
    const area = Math.max(p.width * p.height, c.width * c.height);
    const score = area * distance;
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  }
  // Every source lacked rects — name the first rather than nothing.
  return best ?? pool[0] ?? null;
}

/**
 * A short, stable identifier for the element that shifted.
 *
 * Deliberately not a full CSS path: this string lands in an analytics label,
 * where a 200-character selector is unreadable and unaggregatable. Tag plus id
 * plus the first class is enough to find it in the source, and stays the same
 * across builds — which a nth-child path would not, so the same culprit would
 * fragment into a dozen distinct labels and rank as noise.
 */
export function describeShiftSource(source: ShiftSource): string {
  const node = source.node;
  if (!node || typeof node !== 'object' || !('tagName' in node)) return 'unknown';
  const el = node as Element;
  const tag = String(el.tagName || '').toLowerCase();
  if (!tag) return 'unknown';
  const id = el.id ? `#${el.id}` : '';
  // className is an SVGAnimatedString on SVG elements, hence the String() and
  // the type check rather than a direct .split.
  const raw = typeof el.className === 'string' ? el.className : '';
  const first = raw.trim().split(/\s+/)[0];
  const cls = first ? `.${first}` : '';
  return `${tag}${id}${cls}`.slice(0, 80);
}
